import {
  ALM_COOKIE_KEY,
  CONNECT_ACCESS_TOKEN_KEY,
  CONNECT_ID_TOKEN_KEY,
  ECOM_COOKIE_KEY,
} from '@config/constants';
import { CartCookies } from '@types';
import { customParseJwt } from './parsing';
import { SiteConfig } from './SiteConfig';

interface CartCookieKeys {
  cartIdKey: string;
  cartTotalItemsKey: string;
}

export const getExpireDate = (exp: number) => new Date(exp * 1000).toUTCString();

export const getCookieValue = (name: string): string => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return (
    document.cookie
      .match(new RegExp(`(^|;)\\s*${escapedName}\\s*=\\s*([^;]+)`))
      ?.pop() || ''
  );
};

export const getCookieDomain = (cookieKey: string) => {
  const { cookieDomain, isAigateway } = SiteConfig.getConfig();
  if (isAigateway
      && (cookieKey === CONNECT_ACCESS_TOKEN_KEY
      || cookieKey === CONNECT_ID_TOKEN_KEY)
  ) {
    return '.subscribe.wiley.com';
  }
  return cookieDomain;
};

export const setCookieValue = (key: string, value: string, exp: number) => {
  const domain = `domain=${getCookieDomain(key)}`;
  document.cookie = `${key}=${value}; expires=${getExpireDate(exp)}; ${domain}; path=/`;
};

export const deleteCookie = (name: string) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
};

export const getCartCookieKeys = (): CartCookieKeys => {
  const { siteId, locale } = SiteConfig.getConfig();
  const cartIdKey = `${siteId}-cart-${locale}`;
  const cartTotalItemsKey = `${siteId}-cart-total-items-${locale}`;

  return { cartIdKey, cartTotalItemsKey };
};

export const getCartCookies = (): CartCookies => {
  const cookieKeys = getCartCookieKeys();
  const almToken = getCookieValue(ALM_COOKIE_KEY);
  const ecomToken = getCookieValue(ECOM_COOKIE_KEY);
  const connectAccessToken = getCookieValue(CONNECT_ACCESS_TOKEN_KEY);
  const connectIdToken = getCookieValue(CONNECT_ID_TOKEN_KEY);
  const cartId = getCookieValue(cookieKeys.cartIdKey);
  const cartTotalItems = getCookieValue(cookieKeys.cartTotalItemsKey);

  return {
    cartId,
    almToken,
    ecomToken,
    connectAccessToken,
    connectIdToken,
    cartTotalItems,
  };
};

export const updateCookies = (almToken: string, ecomToken: string = '') => {
  const cookieKeys = getCartCookieKeys();
  const cartCookies = getCartCookies();

  const almInfo = customParseJwt(almToken);
  const oneHourFromNow = Date.now() / 1000 + 60 * 60;
  const exp = almInfo?.exp || oneHourFromNow;

  setCookieValue(ECOM_COOKIE_KEY, ecomToken, exp);
  setCookieValue(cookieKeys.cartIdKey, cartCookies.cartId, exp);
  setCookieValue(cookieKeys.cartTotalItemsKey, cartCookies.cartTotalItems, exp);
};

export const updateCartCookies = (qty: number) => {
  const { cartTotalItemsKey, cartIdKey } = getCartCookieKeys();
  const cartCookies = getCartCookies();

  const oneHourFromNow = Date.now() / 1000 + 60 * 60;

  setCookieValue(cartTotalItemsKey, String(qty), oneHourFromNow);
  setCookieValue(cartIdKey, cartCookies.cartId, oneHourFromNow);
};

export const getBacklinkUrl = (): string => {
  const { siteId } = SiteConfig.getConfig();
  const backlinkCookie = getCookieValue(`${siteId}-backlink`);
  return backlinkCookie || SiteConfig.buildContinueShoppingUrl();
};
